import React from "react";
import { Button } from "react-bootstrap";
import {
  Circle,
  CheckCircle2,
  Video,
  Link2,
  FileText,
} from "lucide-react";

/**
 * TopicRow
 *
 * Single topic row inside a SyllabusDayCard.
 *
 * Props:
 *   - index          : Number  (0-based topic index)
 *   - topic          : Object  { name, status, recordings: [...] }
 *   - sessionStatus  : String  (parent session status)
 *   - onToggle       : Function — toggle this topic's completed/pending status
 *   - onMapRecording : Function — open map recording modal for this topic
 *   - onMapMaterial  : Function — open map material modal for this topic
 *   - linkedMaterialCount : Number — count of materials linked to this topic
 */
const TopicRow = ({ index, topic, sessionStatus, onToggle, onMapRecording, onMapMaterial, linkedMaterialCount = 0 }) => {
  const isCompleted = topic.status === "completed";
  const recordingsCount = topic.recordings?.length || 0;
  const hasRecordings = recordingsCount > 0;
  const hasMaterials = linkedMaterialCount > 0;

  // Topic is clickable (toggleable) only during in_progress session
  const isToggleable = sessionStatus === "in_progress";

  // Show action buttons when session is in_progress or completed
  const showActions = sessionStatus === "in_progress" || sessionStatus === "completed";

  return (
    <div className={`topic-row ${isCompleted ? "topic-completed" : ""} ${isToggleable ? "topic-toggleable" : ""}`}>
      {/* Left: clickable area for toggle */}
      <div
        className="topic-row-left"
        onClick={isToggleable ? onToggle : undefined}
        role={isToggleable ? "button" : undefined}
        title={isToggleable ? `Click to mark as ${isCompleted ? "pending" : "completed"}` : undefined}
      >
        <span className="topic-index">{index + 1}.</span>
        <span className={`topic-status-icon ${isCompleted ? "completed" : "pending"}`}>
          {isCompleted ? (
            <CheckCircle2 size={16} />
          ) : (
            <Circle size={16} />
          )}
        </span>
        <span className="topic-name">{topic.name}</span>
      </div>

      {/* Right: recordings/materials info + action buttons */}
      <div className="topic-row-right">
        {hasRecordings && (
          <span className="topic-recording-linked">
            <Link2 size={13} />
            {recordingsCount === 1 ? "1 recording" : `${recordingsCount} recordings`}
          </span>
        )}
        {hasMaterials && (
          <span className="topic-recording-linked">
            <FileText size={13} />
            {linkedMaterialCount === 1 ? "1 material" : `${linkedMaterialCount} materials`}
          </span>
        )}
        {showActions && (
          <>
            <Button
              variant="outline-primary"
              size="sm"
              className="topic-map-btn"
              onClick={onMapRecording}
            >
              <Video size={14} className="me-1" />
              {hasRecordings ? "Add Recording" : "Map Recording"}
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              className="topic-map-btn"
              onClick={onMapMaterial}
            >
              <FileText size={14} className="me-1" />
              {hasMaterials ? "Add Material" : "Link Material"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default TopicRow;
